import {
  OpsEmployeeCustomerRolesMutationResponseSchema,
  OpsEmployeeCustomerRolesUpdateSchema,
  OpsCustomerCreateResponseSchema,
  OpsCustomerCreateSchema,
  OpsCustomerResponseSchema,
  OpsCustomersResponseSchema,
  OpsEmployeeCreateSchema,
  OpsEmployeeMutationResponseSchema,
  OpsEmployeeRolesUpdateSchema,
  OpsEmployeesResponseSchema,
  OpsEmployeeStatusUpdateSchema,
  OpsMeResponseSchema,
  OpsMcpKeyCreateResponseSchema,
  OpsMcpKeyCreateSchema,
  OpsMcpKeyRevokeResponseSchema,
  OpsMcpKeysResponseSchema,
  OpsProvisioningRetryResponseSchema,
  OpsProvisioningRunsResponseSchema,
  OpsSupportSessionCreateSchema,
  OpsSupportSessionRevokeResponseSchema,
  OpsSupportSessionResponseSchema,
  OpsSupportSessionsResponseSchema,
  OpsSupportLaunchCreateSchema,
  OpsSupportLaunchResponseSchema,
  OpsSupportTargetsResponseSchema,
  OpsWorkspaceAddressUpdateSchema,
  type OpsCustomerCreate,
  type OpsEmployeeCreate,
  type OpsCustomerPageSize,
  type OpsWorkspaceAddressUpdate,
  type OpsEmployeePageSize,
  type OpsEmployeeStatus,
  type OpsMcpKeyCreate,
} from "../../shared/operations-schemas";
import {
  CustomerContextItemInputSchema,
  CustomerContextSummarySchema,
  CustomerDealInputSchema,
  CustomerPersonInputSchema,
  CustomerProfileInputSchema,
  CustomerProfileResponseSchema,
  CustomerRecordCreateResponseSchema,
  type CustomerContextItemInput,
  type CustomerDealInput,
  type CustomerPersonInput,
  type CustomerProfileInput,
} from "../../shared/customer-record-schemas";
import {
  OpsPeopleResponseSchema,
  OrganizationOverviewResponseSchema,
} from "../../shared/organization-overview-schemas";
import {
  ArtifactMetadataSchema,
  ArtifactResponseSchema,
  ArtifactUploadPreparedSchema,
  CustomerContextSearchRequestSchema,
  CustomerContextSearchResponseSchema,
  type ArtifactMetadata,
  type ArtifactScope,
} from "../../shared/customer-context-schemas";
import { ApiClientError, parseResponse } from "../api/client-core";

import { ArtifactPageSchema, NotePageSchema } from "../../shared/customer-retrieval-schemas";

const API_ROOT = "/api/ops";

export async function getOpsMe() {
  return parseResponse(
    await fetch(`${API_ROOT}/me`, { credentials: "same-origin" }),
    OpsMeResponseSchema,
  );
}

export async function listOpsMcpKeys() {
  return parseResponse(
    await fetch(`${API_ROOT}/mcp-keys`, { credentials: "same-origin" }),
    OpsMcpKeysResponseSchema,
  );
}

export async function createOpsMcpKey(input: OpsMcpKeyCreate) {
  return parseResponse(
    await fetch(`${API_ROOT}/mcp-keys`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(OpsMcpKeyCreateSchema.parse(input)),
    }),
    OpsMcpKeyCreateResponseSchema,
  );
}

export async function revokeOpsMcpKey(keyId: string) {
  return parseResponse(
    await fetch(`${API_ROOT}/mcp-keys/${encodeURIComponent(keyId)}`, {
      method: "DELETE",
      credentials: "same-origin",
    }),
    OpsMcpKeyRevokeResponseSchema,
  );
}

export async function listEmployees(input: {
  query?: string;
  page: number;
  pageSize: OpsEmployeePageSize;
}) {
  const query = new URLSearchParams({
    page: String(input.page),
    pageSize: String(input.pageSize),
  });
  if (input.query) query.set("q", input.query);
  return parseResponse(
    await fetch(`${API_ROOT}/employees?${query.toString()}`, { credentials: "same-origin" }),
    OpsEmployeesResponseSchema,
  );
}

export async function grantEmployeeAccess(input: OpsEmployeeCreate) {
  return parseResponse(
    await fetch(`${API_ROOT}/employees`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(OpsEmployeeCreateSchema.parse(input)),
    }),
    OpsEmployeeMutationResponseSchema,
  );
}

export async function updateEmployeeStatus(employeeId: string, status: OpsEmployeeStatus) {
  return parseResponse(
    await fetch(`${API_ROOT}/employees/${encodeURIComponent(employeeId)}/status`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(OpsEmployeeStatusUpdateSchema.parse({ status })),
    }),
    OpsEmployeeMutationResponseSchema,
  );
}

export async function updateEmployeeRoles(employeeId: string, roleKeys: string[]) {
  return parseResponse(
    await fetch(`${API_ROOT}/employees/${encodeURIComponent(employeeId)}/roles`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(OpsEmployeeRolesUpdateSchema.parse({ roleKeys })),
    }),
    OpsEmployeeMutationResponseSchema,
  );
}

export async function updateEmployeeCustomerRoles(
  employeeId: string,
  organizationId: string,
  roleKeys: string[],
) {
  return parseResponse(
    await fetch(`${API_ROOT}/employees/${encodeURIComponent(employeeId)}/customer-roles`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        OpsEmployeeCustomerRolesUpdateSchema.parse({ organizationId, roleKeys }),
      ),
    }),
    OpsEmployeeCustomerRolesMutationResponseSchema,
  );
}

export async function listCustomers(input: {
  query?: string;
  page: number;
  pageSize: OpsCustomerPageSize;
}) {
  const query = new URLSearchParams({
    page: String(input.page),
    pageSize: String(input.pageSize),
  });
  if (input.query) query.set("q", input.query);
  return parseResponse(
    await fetch(`${API_ROOT}/customers?${query.toString()}`, { credentials: "same-origin" }),
    OpsCustomersResponseSchema,
  );
}

export async function getOrganization(customerSlug: string) {
  return parseResponse(
    await fetch(`${API_ROOT}/customers/${encodeURIComponent(customerSlug)}/organization`, {
      credentials: "same-origin",
    }),
    OrganizationOverviewResponseSchema,
  );
}

export async function searchPeople(query: string) {
  return parseResponse(
    await fetch(`${API_ROOT}/people?${new URLSearchParams({ q: query }).toString()}`, {
      credentials: "same-origin",
    }),
    OpsPeopleResponseSchema,
  );
}

export async function getCustomer(customerSlug: string) {
  return parseResponse(
    await fetch(`${API_ROOT}/customers/${encodeURIComponent(customerSlug)}`, {
      credentials: "same-origin",
    }),
    OpsCustomerResponseSchema,
  );
}

export async function updateCustomerWorkspaceAddress(
  customerSlug: string,
  input: OpsWorkspaceAddressUpdate,
) {
  const payload = OpsWorkspaceAddressUpdateSchema.parse(input);
  return parseResponse(
    await fetch(`${API_ROOT}/customers/${encodeURIComponent(customerSlug)}/workspace-address`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),
    OpsCustomerResponseSchema,
  );
}

export async function createCustomer(input: OpsCustomerCreate) {
  const payload = OpsCustomerCreateSchema.parse(input);
  return parseResponse(
    await fetch(`${API_ROOT}/customers`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": `ops-ui-${crypto.randomUUID()}`,
      },
      body: JSON.stringify(payload),
    }),
    OpsCustomerCreateResponseSchema,
  );
}

export async function logoutOps(): Promise<void> {
  const response = await fetch(`${API_ROOT}/session/logout`, {
    method: "POST",
    credentials: "same-origin",
  });
  // An expired/revoked session (including a retry after a lost response) is already signed out.
  if (!response.ok && response.status !== 401) {
    throw new ApiClientError(response.status, "logout_failed", "Could not sign out of operations.");
  }
}

export function opsGoogleSignInHref(): string {
  const path = `${window.location.pathname}${window.location.search}`;
  return `${API_ROOT}/session/google/start?path=${encodeURIComponent(path)}`;
}

export async function listArtifacts(
  organizationId: string,
  scope: ArtifactScope = { kind: "customer" },
  cursor: string | null = null,
) {
  const query = new URLSearchParams({ scope: scope.kind, limit: "50" });
  if (cursor) query.set("cursor", cursor);
  if (scope.kind === "tool") query.set("toolId", scope.toolId);
  return parseResponse(
    await fetch(
      `${API_ROOT}/customers/${encodeURIComponent(organizationId)}/artifacts?${query.toString()}`,
      { credentials: "same-origin" },
    ),
    ArtifactPageSchema,
  );
}

export async function listCustomerNotes(
  organizationId: string,
  scope: ArtifactScope,
  cursor: string | null = null,
) {
  const query = new URLSearchParams({ scope: scope.kind, limit: "50" });
  if (scope.kind === "tool") query.set("toolId", scope.toolId);
  if (cursor) query.set("cursor", cursor);
  return parseResponse(
    await fetch(
      `${API_ROOT}/customers/${encodeURIComponent(organizationId)}/context/notes?${query}`,
      { credentials: "same-origin" },
    ),
    NotePageSchema,
  );
}

export async function prepareArtifactUpload(organizationId: string, input: ArtifactMetadata) {
  const metadata = ArtifactMetadataSchema.parse(input);
  return parseResponse(
    await fetch(
      `${API_ROOT}/customers/${encodeURIComponent(organizationId)}/artifacts/reservations`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(metadata),
      },
    ),
    ArtifactUploadPreparedSchema,
  );
}

export async function uploadArtifact(
  organizationId: string,
  reservation: { artifactId: string; uploadToken: string },
  file: File,
) {
  return parseResponse(
    await fetch(
      `${API_ROOT}/customers/${encodeURIComponent(organizationId)}/artifacts/${encodeURIComponent(reservation.artifactId)}/original`,
      {
        method: "PUT",
        credentials: "same-origin",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          "X-Upload-Token": reservation.uploadToken,
        },
        body: file,
      },
    ),
    ArtifactResponseSchema,
  );
}

export function artifactOriginalHref(organizationId: string, artifactId: string): string {
  return `${API_ROOT}/customers/${encodeURIComponent(organizationId)}/artifacts/${encodeURIComponent(artifactId)}/original`;
}

export function artifactPreviewHref(organizationId: string, artifactId: string): string {
  return `${API_ROOT}/customers/${encodeURIComponent(organizationId)}/artifacts/${encodeURIComponent(artifactId)}/preview`;
}

export async function listCustomerContext(organizationId: string) {
  return parseResponse(
    await fetch(`${API_ROOT}/customers/${encodeURIComponent(organizationId)}/context?notes=none`, {
      credentials: "same-origin",
    }),
    CustomerContextSummarySchema,
  );
}

export async function saveCustomerProfile(organizationId: string, input: CustomerProfileInput) {
  return parseResponse(
    await fetch(`${API_ROOT}/customers/${encodeURIComponent(organizationId)}/context/profile`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(CustomerProfileInputSchema.parse(input)),
    }),
    CustomerProfileResponseSchema,
  );
}

async function createContextRecord(
  organizationId: string,
  collection: "people" | "deals" | "context-items",
  input: unknown,
) {
  return parseResponse(
    await fetch(
      `${API_ROOT}/customers/${encodeURIComponent(organizationId)}/context/${collection}`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      },
    ),
    CustomerRecordCreateResponseSchema,
  );
}

export function createCustomerPerson(organizationId: string, input: CustomerPersonInput) {
  return createContextRecord(organizationId, "people", CustomerPersonInputSchema.parse(input));
}

export function createCustomerDeal(organizationId: string, input: CustomerDealInput) {
  return createContextRecord(organizationId, "deals", CustomerDealInputSchema.parse(input));
}

export function createCustomerContextItem(organizationId: string, input: CustomerContextItemInput) {
  return createContextRecord(
    organizationId,
    "context-items",
    CustomerContextItemInputSchema.parse(input),
  );
}

export async function searchCustomerContext(
  organizationId: string,
  input: { query: string; limit: number; scope?: ArtifactScope },
) {
  return parseResponse(
    await fetch(`${API_ROOT}/customers/${encodeURIComponent(organizationId)}/context/search`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        CustomerContextSearchRequestSchema.parse({ ...input, includeCoverage: true }),
      ),
    }),
    CustomerContextSearchResponseSchema,
  );
}

export async function listProvisioningRuns() {
  return parseResponse(
    await fetch(`${API_ROOT}/provisioning-runs`, { credentials: "same-origin" }),
    OpsProvisioningRunsResponseSchema,
  );
}

export async function retryProvisioningRun(runId: string) {
  return parseResponse(
    await fetch(`${API_ROOT}/provisioning-runs/${encodeURIComponent(runId)}/retry`, {
      method: "POST",
      credentials: "same-origin",
    }),
    OpsProvisioningRetryResponseSchema,
  );
}

export { listOperationsAudit as listAuditEvents } from "../api/audit-client";

export async function listSupportSessions(organizationId: string) {
  return parseResponse(
    await fetch(`${API_ROOT}/customers/${encodeURIComponent(organizationId)}/support-sessions`, {
      credentials: "same-origin",
    }),
    OpsSupportSessionsResponseSchema,
  );
}

export async function startSupportSession(
  organizationId: string,
  input: {
    reason: string;
    targetUserId?: string;
    mode: "read" | "write";
    confirmWrite: boolean;
  },
) {
  return parseResponse(
    await fetch(`${API_ROOT}/customers/${encodeURIComponent(organizationId)}/support-sessions`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(OpsSupportSessionCreateSchema.parse(input)),
    }),
    OpsSupportSessionResponseSchema,
  );
}

export async function listSupportTargets(organizationId: string) {
  return parseResponse(
    await fetch(`${API_ROOT}/customers/${encodeURIComponent(organizationId)}/support-targets`, {
      credentials: "same-origin",
    }),
    OpsSupportTargetsResponseSchema,
  );
}

export async function launchSupportTool(
  organizationId: string,
  supportSessionId: string,
  input: { appInstanceId: string; intendedPath: string },
) {
  return parseResponse(
    await fetch(
      `${API_ROOT}/customers/${encodeURIComponent(organizationId)}/support-sessions/${encodeURIComponent(supportSessionId)}/launch`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(OpsSupportLaunchCreateSchema.parse(input)),
      },
    ),
    OpsSupportLaunchResponseSchema,
  );
}

export async function revokeSupportSession(organizationId: string, supportSessionId: string) {
  return parseResponse(
    await fetch(
      `${API_ROOT}/customers/${encodeURIComponent(organizationId)}/support-sessions/${encodeURIComponent(supportSessionId)}`,
      { method: "DELETE", credentials: "same-origin" },
    ),
    OpsSupportSessionRevokeResponseSchema,
  );
}
